import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

function LangToggle({ lang, setLang, label }) {
  const handleKey = (e) => {
    if (!["ArrowLeft", "ArrowRight", " ", "Enter"].includes(e.key)) return;
    e.preventDefault();
    if (e.key === "ArrowRight") setLang("en");
    else if (e.key === "ArrowLeft") setLang("pt");
    else setLang(lang === "pt" ? "en" : "pt");
  };

  return (
    <div
      role="group"
      aria-label={label}
      onKeyDown={handleKey}
      className="relative inline-grid grid-cols-2 items-center rounded-full h-10 min-w-[110px] px-1 bg-[#F3F5F6] select-none"
    >
      <div
        aria-hidden="true"
        className={[
          "absolute top-1 left-1 h-8 w-[calc(50%-4px)] rounded-full transition-transform duration-200",
          "bg-[#4CB1A1]",
          lang === "en" ? "translate-x-full" : "translate-x-0",
        ].join(" ")}
      />
      <button
        type="button"
        aria-pressed={lang === "pt"}
        onClick={() => setLang("pt")}
        className={[
          "relative z-10 h-8 text-[16px] font-semibold rounded-full outline-none",
          lang === "pt" ? "text-white" : "text-[#111]",
          "focus-visible:ring-2 focus-visible:ring-[#4CB1A1] focus-visible:ring-offset-2",
        ].join(" ")}
      >
        PT
      </button>
      <button
        type="button"
        aria-pressed={lang === "en"}
        onClick={() => setLang("en")}
        className={[
          "relative z-10 h-8 text-[16px] font-semibold rounded-full outline-none",
          lang === "en" ? "text-white" : "text-[#111]",
          "focus-visible:ring-2 focus-visible:ring-[#4CB1A1] focus-visible:ring-offset-2",
        ].join(" ")}
      >
        EN
      </button>
    </div>
  );
}

export default function Navbar({ activeLang = "pt", onLanguageChange }) {
  const [lang, setLang] = useState(activeLang);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setLang(activeLang);
  }, [activeLang]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const updateLang = (valueOrUpdater) => {
    setLang((prev) => {
      const next = typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
      if (typeof onLanguageChange === "function" && next !== prev) {
        onLanguageChange(next);
      } else if (typeof valueOrUpdater !== "function" && next === prev && typeof onLanguageChange === "function") {
        // if same value passed directly, still notify parent to ensure sync
        onLanguageChange(next);
      }
      return next;
    });
  };

  const t = {
    pt: {
      about: "SOBRE NÓS",
      innovationVoucher: "APOIO PRR",
      contact: "CONTACTOS",
      toggleLabel: "Selecionar idioma",
    },
    en: {
      about: "ABOUT US",
      innovationVoucher: "SUPPORT PRR",
      contact: "CONTACT",
      toggleLabel: "Select language",
    },
  };
  const basePath = lang === "en" ? "/en" : lang === "pt" ? "/pt" : "/";
  const normalizedBasePath = basePath === "/" ? "" : basePath;
  const sectionHref = (hash) => `${basePath}${hash}`;
  const prrHref = `${normalizedBasePath}/PRR`;
  const isOnPRRPage = location.pathname.toLowerCase().includes("/prr");
  const prrLinkClasses = [
    "group relative inline-flex items-center text-[16px] font-medium tracking-[0.2px]",
    isOnPRRPage ? "text-[#4CB1A1]" : "text-[#111]",
    "hover:text-[#4CB1A1]",
  ].join(" ");
  const prrBadgeSrc = isOnPRRPage ? "/assets/new_blue.png" : "/assets/new.png";
  const prrBadgeStyle = {
    top: "-16px",
    right: "-16px",
    height: "20px",
  };

  return (
    <header
      className={[
        "fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-sm transition-colors duration-300",
        scrolled ? "border-b border-slate-200/80 shadow-[0_6px_18px_-12px_rgba(30,42,90,0.3)]" : "border-b border-transparent",
      ].join(" ")}
    >
<nav className="mx-auto max-w-[1900px] px-2 sm:px-4 md:px-8 lg:px-12">
        <div className="h-[88px] flex items-center justify-between">

          <a href={basePath} aria-label="Invisible Cloud - Início" className="inline-block leading-none ml-4 md:ml-8 lg:ml-12">
            <img src="/assets/logo.png" alt="Invisible Cloud" className="h-[30px] w-auto" />
          </a>

          {/* Desktop: */}
          <div className="hidden md:flex items-center gap-10 lg:gap-7">
            <a
              href={sectionHref("#about")}
              className="text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
            >
              {t[lang].about}
            </a>
       
            <a
              href={sectionHref("#footer")}
              className="text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
            >
              {t[lang].contact}
            </a>

            <a href={prrHref} className={prrLinkClasses}>
              <span className="relative">
                {t[lang].innovationVoucher}
                <img
                  src={prrBadgeSrc}
                  alt=""
                  aria-hidden="true"
                  className={`pointer-events-none absolute transition-transform ${isOnPRRPage ? "" : "group-hover:scale-110"}`}
                  style={prrBadgeStyle}
                />
              </span>
            </a>

            <LangToggle lang={lang} setLang={updateLang} label={t[lang].toggleLabel} />
          </div>

          {/* Mobile: botão hambúrguer à direita */}
          <button
            type="button"
            aria-label="Abrir menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-200 hover:bg-slate-50"
          >
            <span className="sr-only">Menu</span>
            {/* ícone hambúrguer / close */}
            <svg
              className={`h-5 w-5 ${open ? "hidden" : "block"}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg
              className={`h-5 w-5 ${open ? "block" : "hidden"}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        {/* Painel mobile */}
        <div
          id="mobile-menu"
          className={[
            "md:hidden overflow-hidden transition-[max-height] duration-300",
            open ? "max-h-96" : "max-h-0",
          ].join(" ")}
        >
          <div className="pb-4 pt-2 border-t border-slate-200">
            <div className="flex flex-col gap-3 items-end">
              <a
                href={sectionHref("#about")}
                className="text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
                onClick={() => setOpen(false)}
              >
                {t[lang].about}
              </a>
              <a href={prrHref} className={prrLinkClasses} onClick={() => setOpen(false)}>
                <span className="relative">
                  {t[lang].innovationVoucher}
                  <img
                    src={prrBadgeSrc}
                    alt=""
                    aria-hidden="true"
                    className={`pointer-events-none absolute transition-transform ${isOnPRRPage ? "" : "group-hover:scale-110"}`}
                    style={prrBadgeStyle}
                  />
                </span>
              </a>
              <a
                href={sectionHref("#footer")}
                className="text-[16px] font-medium tracking-[0.2px] text-[#111] hover:text-[#4CB1A1]"
                onClick={() => setOpen(false)}
              >
                {t[lang].contact}
              </a>
              <LangToggle lang={lang} setLang={updateLang} label={t[lang].toggleLabel} />
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
